import { useEffect, useState } from "react";
import { Zap, X, Download, Share, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";

const DISMISS_KEY = "nexolog_pwa_dismissed";

function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}
function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
}

export default function InstallPWA() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY) === "1") return;

    const onBIP = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBIP);

    // iOS não dispara beforeinstallprompt — mostramos instruções manuais.
    if (isIOS()) {
      const t = setTimeout(() => { setIosHint(true); setShow(true); }, 1500);
      return () => { window.removeEventListener("beforeinstallprompt", onBIP); clearTimeout(t); };
    }
    const onInstalled = () => setShow(false);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    setShow(false);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") setShow(false);
    setDeferred(null);
  };

  if (!show) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] pb-[calc(env(safe-area-inset-bottom)+4.5rem)] lg:pb-4 px-3 pointer-events-none" data-testid="pwa-install-banner">
      <div className="pointer-events-auto mx-auto max-w-md rounded-xl border border-primary/40 bg-card/95 backdrop-blur-xl shadow-[0_8px_30px_rgba(0,0,0,0.5)] p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center shrink-0 shadow-[0_0_18px_rgba(245,158,11,0.35)]">
            <Zap className="w-5 h-5 text-primary-foreground" strokeWidth={2.5} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-display font-bold uppercase text-sm leading-tight">Instalar o NEXO LOG</div>
            {iosHint ? (
              <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
                Toque em <Share className="inline w-3.5 h-3.5 -mt-0.5" /> <b>Compartilhar</b> e depois em
                <span className="inline-flex items-center gap-0.5"> <Plus className="inline w-3.5 h-3.5" /> <b>Adicionar à Tela de Início</b></span>.
              </div>
            ) : (
              <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
                Adicione o app à tela inicial para abrir em tela cheia, com ícone próprio e acesso rápido.
              </div>
            )}
          </div>
          <button onClick={dismiss} className="text-muted-foreground hover:text-foreground shrink-0" data-testid="pwa-dismiss" aria-label="Fechar">
            <X className="w-4 h-4" />
          </button>
        </div>
        {!iosHint && (
          <div className="flex gap-2 mt-3">
            <Button onClick={install} className="flex-1" data-testid="pwa-install-btn">
              <Download className="w-4 h-4" /> Instalar app
            </Button>
            <Button variant="outline" onClick={dismiss} data-testid="pwa-later-btn">Agora não</Button>
          </div>
        )}
      </div>
    </div>
  );
}
